import Tool, { type ToolArgs } from '../../../tools/Tool';
import React, { useRef, useEffect } from 'react';
import { Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import styles from './EditLinkCard.module.css';
import { isLinkCardElement } from '../../../types/guards';
import type { Placement } from '@floating-ui/dom';
import MediaCommon from '../../MediaPlugin/MediaCommon';
import History from '../../../collaboration/History';
import { ALLOWED_IMAGE_TYPES } from '../../MediaPlugin/tools/ToolInsertMedia';
import AhaEditor from '../../../AhaEditor';
import imageUpload from '../../../assets/icons/imageUpload.svg';
import imageRemove from '../../../assets/icons/imageRemove.svg';
import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';

// Convert from Javascript File object to a data URL.
const convertFileToDataURL = async (file: File): Promise<string> => {
  const reader = new FileReader();

  return await new Promise((resolve, reject) => {
    reader.onerror = () => {
      reader.abort();
      reject(new DOMException('Problem parsing image file.'));
    };

    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.readAsDataURL(file);
  });
};

const UploadImageLinkCardModal: React.FC<ToolArgs> = ({
  editor,
  element,
  toolbarHideModal,
}) => {
  const uploaderRef = useRef<HTMLElement>(null);

  const handleFileUpload = async (file: File) => {
    try {
      // Validate file type
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        throw new Error(
          'Please select a valid image file (JPG, PNG, GIF, or WebP)'
        );
      }

      // Convert to data URL and store temporarily
      const dataUrl = await convertFileToDataURL(file);
      const temporaryDataUrl = MediaCommon.storeTemporaryData(dataUrl);

      // Update the element with temporary URL
      if (!element) return;
      const path = ReactEditor.findPath(editor, element);
      Transforms.setNodes(
        editor,
        { linkCard_image: temporaryDataUrl },
        { at: path }
      );

      // Remember the current undo entry
      const history = History.getForEditor(editor);
      const undoEntry = history.lastUndoEntry();

      // Upload file using the media provider
      const ahaEditor = AhaEditor.getForEditor(editor);
      if (!ahaEditor) throw new Error('Editor not available');

      const mediaProvider = ahaEditor.getProvider('media');
      if (!mediaProvider) throw new Error('Media provider not available');

      const attachmentUrl = await mediaProvider.onUploadFile(file);

      // Augment the undo entry and replace temporary URL with real URL
      history.addUndo(editor, undoEntry, () => {
        if (attachmentUrl) {
          Transforms.setNodes(
            editor,
            { linkCard_image: attachmentUrl },
            {
              at: [],
              match: (n) =>
                isLinkCardElement(n) && n.linkCard_image === temporaryDataUrl,
            }
          );
        } else {
          // Upload failed, remove the temporary image
          Transforms.setNodes(
            editor,
            { linkCard_image: undefined },
            {
              at: [],
              match: (n) =>
                isLinkCardElement(n) && n.linkCard_image === temporaryDataUrl,
            }
          );
        }
      });
    } catch (err) {}
  };

  useEffect(() => {
    const uploader = uploaderRef.current;
    if (!uploader) return;
    uploader.addEventListener(
      'aha-uploader:upload-change',
      (event: CustomEvent) => {
        handleFileUpload(event.detail[0]);
      }
    );
  }, []);

  if (!element || !isLinkCardElement(element)) return null;

  const handleRemoveImage = () => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.setNodes(
      editor,
      {
        linkCard_image: '', // Set to empty string (rather than undefined) to indicate removal. This way we know that the user explicitly removed the image, and wants no image or placeholder image at all.
      },
      { at: path }
    );
  };

  return (
    <div className={styles.editLink}>
      <div className={styles.uploadSection}>
        <aha-uploader
          ref={uploaderRef}
          className={styles.uploader}
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple={false}
          hide-uploaded-files={true}
        >
          <span slot="dropzone-icon" />
          <span slot="dropzone-button">
            upload image <i className="fa-regular fa-arrow-up-from-bracket" />
          </span>
        </aha-uploader>
      </div>

      {element.linkCard_image && element.linkCard_image !== null && (
        <div>
          <aha-button
            kind="link"
            role="button"
            onClick={handleRemoveImage}
            class={styles.removeImageButton}
          >
            <span className={styles.buttonText}>
              {svgToIcon(imageRemove)} Remove image
            </span>
          </aha-button>
        </div>
      )}
    </div>
  );
};

// Tool for uploading images to LinkCard elements
class ToolUploadImageLinkCard extends Tool {
  static toolName = 'Upload image';
  static withoutRefocus = true;

  // Show the modal to upload/manage images
  static action({ editor, element, toolbarShowModal }: ToolArgs): void {
    toolbarShowModal?.(ToolUploadImageLinkCard);
  }

  static renderToolbarButtonIcon({ editor, element }: ToolArgs): JSX.Element {
    return svgToIcon(imageUpload);
  }

  static renderToolbarModal(toolArgs: ToolArgs): JSX.Element {
    return <UploadImageLinkCardModal {...toolArgs} />;
  }

  static tooltipPlacement(): Placement {
    return 'bottom';
  }
}

export default ToolUploadImageLinkCard;
